import { useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AuthLayout } from '../components/templates/AuthLayout';
import { Alert } from '../components/atoms/Alert/Alert';
import { Button } from '../components/atoms/Button/Button';
import { Stack } from '../components/atoms/Stack/Stack';
import { Text } from '../components/atoms/Typography/Text';
import { useTranslation } from '../i18n/useTranslation';
import { ApiError } from '../lib/apiClient';
import { confirmEmailSubscription, unsubscribeByToken, type EmailKind } from '../services/emailPreferencesService';
import './EmailLinkPage.css';

export type EmailLinkAction = 'confirm' | 'unsubscribe';

type Status = 'idle' | 'working' | 'done' | 'invalid' | 'expired' | 'stale' | 'error';

function failureStatus(err: unknown): Status {
  if (err instanceof ApiError) {
    if (err.status === 410) return 'expired';
    if (err.status === 409) return 'stale';
    if (err.status === 400) return 'invalid';
  }
  return 'error';
}

/**
 * Landing pages of the links in our e-mails (public, no login):
 *  - /email/confirm?token=…     double opt-in for product updates
 *  - /email/unsubscribe?token=… unsubscribe from one kind of e-mail
 * Nothing happens on page load: the user confirms with a button, so mail
 * scanners that open links cannot confirm or unsubscribe on their behalf. (Mail
 * clients use the RFC 8058 one-click POST of the List-Unsubscribe header.)
 */
export function EmailLinkPage({ action }: { action: EmailLinkAction }) {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [status, setStatus] = useState<Status>(token ? 'idle' : 'invalid');
  const [kind, setKind] = useState<EmailKind | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const prefix = action === 'confirm' ? 'emailLinks.confirm' : 'emailLinks.unsubscribe';

  async function run(): Promise<void> {
    setStatus('working');
    try {
      if (action === 'confirm') {
        await confirmEmailSubscription(token);
      } else {
        setKind(await unsubscribeByToken(token));
      }
      setStatus('done');
    } catch (err: unknown) {
      setStatus(failureStatus(err));
    }
    // Move focus to the outcome so screen-reader and keyboard users get it at once.
    window.setTimeout(() => resultRef.current?.focus(), 0);
  }

  const doneMessage =
    action === 'confirm'
      ? t('emailLinks.confirm.done')
      : kind === 'task_reminders'
        ? t('emailLinks.unsubscribe.doneTaskReminders')
        : kind === 'critical_alerts'
          ? t('emailLinks.unsubscribe.doneCriticalAlerts')
          : t('emailLinks.unsubscribe.doneProductUpdates');

  return (
    <AuthLayout title={t(`${prefix}.title`)}>
      <Stack gap="4">
        {(status === 'idle' || status === 'working') && (
          <>
            <Text>{t(`${prefix}.question`)}</Text>
            <Button variant="primary" onClick={() => void run()} isLoading={status === 'working'} className="email-link-page__action">
              {t(`${prefix}.button`)}
            </Button>
          </>
        )}
        <div ref={resultRef} tabIndex={-1} className="email-link-page__result">
          {status === 'done' && (
            // live="off": focus moves to the outcome, which reads it (never announce twice).
            <Alert tone="success" live="off">
              {doneMessage}
            </Alert>
          )}
          {(status === 'invalid' || status === 'expired' || status === 'stale' || status === 'error') && (
            <Alert live={status === 'invalid' && !token ? 'polite' : 'off'}>{t(`emailLinks.errors.${status}`)}</Alert>
          )}
        </div>
        {(status === 'done' || status === 'expired' || status === 'stale') && (
          <Text size="sm">
            {t('emailLinks.settingsHint')}{' '}
            <Link to="/settings#email-notifications" className="email-link-page__link">
              {t('emailLinks.settingsLink')}
            </Link>
          </Text>
        )}
      </Stack>
    </AuthLayout>
  );
}

export function EmailConfirmPage() {
  return <EmailLinkPage action="confirm" />;
}

export function EmailUnsubscribePage() {
  return <EmailLinkPage action="unsubscribe" />;
}

export default EmailLinkPage;
